/**
 * InboundLoadsManagement.jsx
 * 
 * Integrated component for processing inbound loads directly on the warehouse page:
 * - Select customer (filters products and pallets)
 * - Search products by name, code, or batch
 * - Add multiple products to multiple pallets
 * - Review and submit entire batch
 * - Styled to match warehouse and orders components
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Button, Form, Row, Col, Card, Badge, Table, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faBoxes, faWarehouse, faTrash, faList } from '@fortawesome/free-solid-svg-icons';
import { ActivityIndicator } from '../custom/ActivityIndicator';
import logger from "../../utils/logger";

const InboundLoadsManagement = ({ 
    theme = "light", 
    warehouseCustomers = [], 
    activePallets = [], 
    activeProducts = [],
    selectedCustomerId = "", // Selected customer from parent (warehouse management)
    warehouseSummaryProducts = [], // Products from warehouse summary with pallet locations
    allCustomerPallets = [], // All customer pallets with occupancy data
    onSubmit,
    onLoadingChange, // Callback to notify parent of loading state changes
    loading = false, // Parent-controlled loading state
    isAdmin = false // Admin flag to allow date editing
}) => {
    // State management - selectedCustomer now comes from props as selectedCustomerId
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedPalletCode, setSelectedPalletCode] = useState('');
    const [quantity, setQuantity] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inboundItems, setInboundItems] = useState([]); // Array of items to be processed
    const [activeTab, setActiveTab] = useState('mixed'); // 'mixed', 'bulk', or 'review'
    
    // Bulk load specific state
    const [bulkProduct, setBulkProduct] = useState('');
    const [bulkSearchTerm, setBulkSearchTerm] = useState('');
    const [debouncedBulkSearchTerm, setDebouncedBulkSearchTerm] = useState('');
    const [palletFilterTerm, setPalletFilterTerm] = useState(''); // Filter for pallet selection
    const [debouncedPalletFilterTerm, setDebouncedPalletFilterTerm] = useState('');
    
    // Refs for uncontrolled inputs
    const bulkSearchRef = useRef(null);
    const palletFilterRef = useRef(null);
    const [selectedPallets, setSelectedPallets] = useState([]); // Array of selected pallet codes
    const [bulkQuantityPerPallet, setBulkQuantityPerPallet] = useState('');
    const [totalBulkPallets, setTotalBulkPallets] = useState('');
    
    // Additional costs state
    const [additionalCosts, setAdditionalCosts] = useState({
        suppliedPallets: false,
        suppliedPalletsQty: 1,
        handledPallets: false,
        handledPalletsQty: 1,
        wrappedPallets: false,
        wrappedPalletsQty: 1,
        bandedPallets: false,
        bandedPalletsQty: 1,
        containerType: null, // null, '20ft', or '40ft'
        customCharges: []
    });

    // Local pallet data fetched fresh on each customer selection for accurate occupancy
    const [localPalletData, setLocalPalletData] = useState([]);

    // Inbound date state - defaults to today
    const [inboundDate, setInboundDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // YYYY-MM-DD format for input
    });

    // Debounce search inputs to improve performance
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300); // 300ms delay

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedBulkSearchTerm(bulkSearchTerm);
        }, 300); // 300ms delay

        return () => clearTimeout(timeoutId);
    }, [bulkSearchTerm]);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedPalletFilterTerm(palletFilterTerm);
        }, 300); // 300ms delay

        return () => clearTimeout(timeoutId);
    }, [palletFilterTerm]);



    // Theme colors - improved for better visibility and interaction
    const isDark = theme === "dark";
    const color = useMemo(() => ({
        bg: isDark ? "#1a1a1a" : "#ffffff",
        panel: isDark ? "#2d2d2d" : "#ffffff",
        fg: isDark ? "#ffffff" : "#212529",
        border: isDark ? "#495057" : "#dee2e6",
        headerBg: isDark ? "#343a40" : "#f8f9fa",
        headerFg: isDark ? "#ffffff" : "#212529",
        highlight: "rgba(26, 221, 247, 0.6)",
        primary: "#0d6efd",
        success: "#198754",
        successBg: isDark ? "#0f5132" : "#d1e7dd",
        warning: "#ffc107",
        warningBg: isDark ? "#664d03" : "#fff3cd",
        danger: "#dc3545",
        muted: "#6c757d",
        inputBg: isDark ? "#495057" : "#ffffff",
        inputBorder: isDark ? "#6c757d" : "#ced4da"
    }), [isDark]);

    // Utility function to format date for display (DD/MM/YYYY)
    const formatDateForDisplay = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Utility function to convert date to ISO format for backend
    const formatDateForBackend = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    };

    // Reset form function
    const resetForm = () => {
        // setSelectedCustomer(''); // Removed - customer comes from parent
        setSelectedProduct('');
        setSelectedPalletCode('');
        setQuantity('');
        setSearchTerm('');
        setInboundItems([]);
        setActiveTab('mixed');
        setBulkProduct('');
        setBulkSearchTerm('');
        setSelectedPallets([]);
        setBulkQuantityPerPallet('');
        setTotalBulkPallets('');
        setAdditionalCosts({
            suppliedPallets: false,
            suppliedPalletsQty: 1,
            handledPallets: false,
            handledPalletsQty: 1,
            wrappedPallets: false,
            wrappedPalletsQty: 1,
            bandedPallets: false,
            bandedPalletsQty: 1,
            containerType: null,
            customCharges: []
        });
        // Reset inbound date to today
        const today = new Date();
        setInboundDate(today.toISOString().split('T')[0]);
    };

    // Filter products by selected customer and search term - use warehouse summary products
    const filteredProducts = useMemo(() => {
        if (!selectedCustomerId) return [];
        
        // Use warehouse summary products if available, otherwise fallback to activeProducts
        const productsToFilter = warehouseSummaryProducts.length > 0 ? warehouseSummaryProducts : activeProducts;
        
        let filtered = productsToFilter.filter(product => 
            product.customer_id === parseInt(selectedCustomerId)
        );

        // Apply search filter only if there's a search term (always show all products for customer initially)
        if (debouncedSearchTerm.trim()) {
            const searchLower = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.product_name?.toLowerCase().includes(searchLower) ||
                product.product_code?.toLowerCase().includes(searchLower) ||
                product.batch_no?.toLowerCase().includes(searchLower)
            );
        }

        // Sort by product name
        return filtered.sort((a, b) => {
            const nameA = (a.product_name || '').toLowerCase();
            const nameB = (b.product_name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [warehouseSummaryProducts, activeProducts, selectedCustomerId, debouncedSearchTerm]);

    // Memoize expensive product calculations
    const selectedBulkProductData = useMemo(() => {
        if (!bulkProduct || !selectedCustomerId) return null;
        return activeProducts.find(p => p.id === bulkProduct && p.customer_id === parseInt(selectedCustomerId));
    }, [bulkProduct, selectedCustomerId, activeProducts]);

    const bulkProductDisplayValue = useMemo(() => {
        if (!selectedBulkProductData) return bulkSearchTerm;
        return `${selectedBulkProductData.product_name} - ${selectedBulkProductData.product_code}${selectedBulkProductData.batch_no ? ` [${selectedBulkProductData.batch_no}]` : ''}`;
    }, [selectedBulkProductData, bulkSearchTerm]);

    // normalize activePallets for searching and filtering
    const searchablePallets = useMemo(() => {
        return (Array.isArray(activePallets) ? activePallets : []).map(pallet => ({
            ...pallet,
            _searchCode: (pallet.pallet_code || '').toLowerCase(),
            _searchLane: String(pallet.lane_id ?? '').toLowerCase(),
        }));
    }, [activePallets]);

    // Filter pallets by selected customer and search term - use allCustomerPallets with occupancy data
    const filteredPallets = useMemo(() => {
        const search = debouncedPalletFilterTerm.trim().toLowerCase();
        if (!search) {
            return searchablePallets;
        }
        return searchablePallets.filter(pallet =>
            pallet._searchCode.includes(search) || pallet._searchLane.includes(search)
        );
    }, [
        searchablePallets,
        debouncedPalletFilterTerm
    ]);

    // Memoize expensive pallet calculations
    const emptyPalletsCount = useMemo(() => {
        return filteredPallets.filter(p => {
            const hasInboundItems = inboundItems.some(item => item.pallet_code === p.pallet_code);
            const isOccupied = p.occupied || (p.items && p.items.length > 0) || p.total_quantity > 0 || hasInboundItems;
            return !isOccupied;
        }).length;
    }, [filteredPallets, inboundItems]);

    // Memoize expensive pallet status calculations for bulk load table
    const palletStatuses = useMemo(() => {
        const statusMap = new Map();
        filteredPallets.forEach(pallet => {
            const hasInboundItems = inboundItems.some(item => item.pallet_code === pallet.pallet_code);
            const isOccupied = pallet.occupied || pallet.total_quantity > 0 || (pallet.items && pallet.items.length > 0) || hasInboundItems;
            const isSelected = selectedPallets.includes(pallet.pallet_code);
            statusMap.set(pallet.pallet_id || pallet.id, { hasInboundItems, isOccupied, isSelected });
        });
        return statusMap;
    }, [filteredPallets, inboundItems, selectedPallets]);

    // Add item to inbound list
    const addItemToInbound = () => {
        if (!selectedCustomerId || !selectedProduct || !selectedPalletCode || !quantity) {
            return;
        }

        // Find product from warehouse summary products or active products for the customer
        const productsToSearch = warehouseSummaryProducts.length > 0 ? warehouseSummaryProducts : activeProducts;
        const product = productsToSearch.find(p => p.id === selectedProduct && p.customer_id === parseInt(selectedCustomerId));
        const pallet = filteredPallets.find(p => p.pallet_code === selectedPalletCode);
        
        if (!product || !pallet) {
            return;
        }

        const newItem = {
            id: Date.now(), // Simple ID for React keys
            customer_id: parseInt(selectedCustomerId),
            product_id: product.id,
            product_name: product.product_name,
            product_code: product.product_code,
            batch_no: product.batch_no,
            pallet_id: pallet.pallet_id || pallet.id,
            pallet_code: pallet.pallet_code,
            lane_id: pallet.lane_id,
            quantity: parseInt(quantity)
        };

        // Check for duplicates (same product + pallet combination)
        const existingIndex = inboundItems.findIndex(item => 
            item.product_id === newItem.product_id && 
            item.pallet_code === newItem.pallet_code
        );

        if (existingIndex >= 0) {
            // Update existing item quantity
            const updatedItems = [...inboundItems];
            updatedItems[existingIndex].quantity += newItem.quantity;
            setInboundItems(updatedItems);
        } else {
            // Add new item
            setInboundItems(prev => [...prev, newItem]);
        }

        // Clear form for next entry
        setSelectedProduct('');
        setSelectedPalletCode('');
        setQuantity('');
        setSearchTerm('');
    };

    // Bulk load functions
    const togglePalletSelection = useCallback((palletCode) => {
        setSelectedPallets(prev => {
            if (prev.includes(palletCode)) {
                return prev.filter(code => code !== palletCode);
            } else {
                return [...prev, palletCode];
            }
        });
    }, []);

    const selectAllEmptyPallets = useCallback(() => {
        if (!bulkProduct) return;
        const isBulkProduct = selectedBulkProductData && (selectedBulkProductData.pallet_spaces || 1) > 1;
        const emptyPallets = filteredPallets.filter(pallet => {
            const hasInboundItems = inboundItems.some(item => item.pallet_code === pallet.pallet_code);
            const isOccupied = pallet.occupied || (pallet.items && pallet.items.length > 0) || pallet.total_quantity > 0 || hasInboundItems;
            const palletType = pallet.pallet_type || null;
            const isBlocked = isBulkProduct && palletType === 'single_sku';
            return !isOccupied && !isBlocked;
        });
        setSelectedPallets(emptyPallets.map(p => p.pallet_code || p.id).filter(Boolean));
        console.log("selected pallets: ", selectedPallets);
    }, [filteredPallets, inboundItems, bulkProduct, selectedBulkProductData]);

    const clearPalletSelection = useCallback(() => {
        setSelectedPallets([]);
    }, []);

    const addBulkLoadToInbound = () => {
        if (!selectedCustomerId || !bulkProduct || selectedPallets.length === 0 || !bulkQuantityPerPallet) {
            return;
        }

        const productsToSearch = warehouseSummaryProducts.length > 0 ? warehouseSummaryProducts : activeProducts;
        const product = productsToSearch.find(p => p.id === bulkProduct && p.customer_id === parseInt(selectedCustomerId));
        if (!product) return;

        const newItems = selectedPallets.map(palletCode => {
            const pallet = filteredPallets.find(p => p.pallet_code === palletCode);
            
            // Safety check - skip this pallet if not found
            if (!pallet) {
                console.warn(`Pallet with code ${palletCode} not found in filteredPallets`);
                return null;
            }
            
            return {
                id: Date.now() + Math.random(), // Unique ID for React keys
                customer_id: parseInt(selectedCustomerId),
                product_id: product.id,
                product_name: product.product_name,
                product_code: product.product_code,
                batch_no: product.batch_no,
                pallet_id: pallet.pallet_id || pallet.id,
                pallet_code: pallet.pallet_code,
                lane_id: pallet.lane_id,
                quantity: parseInt(bulkQuantityPerPallet)
            };
        }).filter(item => item !== null); // Remove any null items

        // Add all new items to inbound list
        setInboundItems(prev => [...prev, ...newItems]);

        // Clear bulk form
        setBulkProduct('');
        setBulkSearchTerm('');
        setSelectedPallets([]);
        setBulkQuantityPerPallet('');
        setTotalBulkPallets('');
    };

    // Filter products for bulk load - use warehouse summary products
    const filteredBulkProducts = useMemo(() => {
        if (!selectedCustomerId) return [];
        
        // Use warehouse summary products if available, otherwise fallback to activeProducts
        const productsToFilter = warehouseSummaryProducts.length > 0 ? warehouseSummaryProducts : activeProducts;
        
        let filtered = productsToFilter.filter(product => 
            product.customer_id === parseInt(selectedCustomerId)
        );

        if (debouncedBulkSearchTerm.trim()) {
            const searchLower = debouncedBulkSearchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.product_name?.toLowerCase().includes(searchLower) ||
                product.product_code?.toLowerCase().includes(searchLower) ||
                product.batch_no?.toLowerCase().includes(searchLower)
            );
        }

        return filtered.sort((a, b) => {
            const nameA = (a.product_name || '').toLowerCase();
            const nameB = (b.product_name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [warehouseSummaryProducts, activeProducts, selectedCustomerId, debouncedBulkSearchTerm]);

    const removeItemFromInbound = useCallback((itemId) => {
        setInboundItems(prev => prev.filter(item => item.id !== itemId));
    }, []);

    const updateItemQuantity = useCallback((itemId, newQuantity) => {
        const quantity = parseInt(newQuantity) || 0;
        if (quantity <= 0) {
            removeItemFromInbound(itemId);
            return;
        }
        
        setInboundItems(prev => 
            prev.map(item => 
                item.id === itemId 
                    ? { ...item, quantity }
                    : item
            )
        );
    }, [removeItemFromInbound]);

    const addCustomCharge = () => {
        setAdditionalCosts(prev => ({
            ...prev,
            customCharges: [
                ...prev.customCharges,
                { id: Date.now(), description: '', amount: '' }
            ]
        }));
    };

    const updateCustomCharge = (id, field, value) => {
        setAdditionalCosts(prev => ({
            ...prev,
            customCharges: prev.customCharges.map(charge =>
                charge.id === id ? { ...charge, [field]: value } : charge
            )
        }));
    };

    const removeCustomCharge = (id) => {
        setAdditionalCosts(prev => ({
            ...prev,
            customCharges: prev.customCharges.filter(charge => charge.id !== id)
        }));
    };

    const hasInvalidCustomCharges = additionalCosts.customCharges.some(charge =>
        !charge.description.trim() || !charge.amount || Number(charge.amount) <= 0
    );

    const customChargesTotal = additionalCosts.customCharges.reduce(
        (total, charge) => total + (Number(charge.amount) || 0),
        0
    );

    // Handle batch submission
    const handleSubmitBatch = async () => {
        if (inboundItems.length === 0 || hasInvalidCustomCharges || !onSubmit) return;

        setIsSubmitting(true);
        onLoadingChange?.(true); // Notify parent of loading state
        
        try {
            const batchData = {
                customer_id: parseInt(selectedCustomerId),
                items: inboundItems.map(item => ({
                    product_id: item.product_id,
                    pallet_code: item.pallet_code,
                    pallet_id: item.pallet_id,
                    quantity: item.quantity
                })),
                additionalCosts: additionalCosts,
                inboundDate: formatDateForBackend(inboundDate) // Pass the date in YYYY-MM-DD format
            };

            await onSubmit(batchData);
            resetForm();
        } catch (error) {
            logger.error('Error submitting inbound batch:', error);
        } finally {
            setIsSubmitting(false);
            // Don't call onLoadingChange(false) here - let parent control when loading stops
        }
    };

    const selectedProductData = useMemo(() => {
        if (!selectedProduct || !selectedCustomerId) {
            return null;
        }
        
        const productsToSearch = warehouseSummaryProducts.length > 0 ? warehouseSummaryProducts : activeProducts;
        const found = productsToSearch.find(p => p.id === selectedProduct && p.customer_id === parseInt(selectedCustomerId));
        return found;
    }, [selectedProduct, selectedCustomerId, warehouseSummaryProducts, activeProducts]);
    
    // Memoized helper function to get customer name
    const getCustomerName = useCallback((customerId) => {
        if (!customerId || !warehouseCustomers) return null;
        const customer = warehouseCustomers.find(c => c.id === parseInt(customerId));
        return customer ? customer.company : `Customer ID: ${customerId}`;
    }, [warehouseCustomers]);
    
    const totalItems = useMemo(() => 
        inboundItems.reduce((sum, item) => sum + item.quantity, 0), 
        [inboundItems]
    );

    // Create optimized event handlers
    const handleSearchTermChange = useCallback((e) => {
        if (selectedProduct) {
            setSelectedProduct('');
        }
        setSearchTerm(e.target.value);
    }, [selectedProduct]);

    const handleQuantityChange = useCallback((e) => {
        setQuantity(e.target.value);
    }, []);

    const handleBulkSearchChange = useCallback((e) => {
        if (bulkProduct) {
            setBulkProduct('');
        }
        // Only update state, don't worry about the input value - it's uncontrolled
        setBulkSearchTerm(e.target.value);
    }, [bulkProduct]);

    const handleBulkQuantityChange = useCallback((e) => {
        setBulkQuantityPerPallet(e.target.value);
    }, []);

    const handleTotalPalletsChange = useCallback((e) => {
        setTotalBulkPallets(e.target.value);
    }, []);

    const handlePalletFilterChange = useCallback((e) => {
        setPalletFilterTerm(e.target.value);
    }, []);

    const handleDateChange = useCallback((e) => {
        setInboundDate(e.target.value);
    }, []);

    // Show disabled state when no customer is selected
    if (!selectedCustomerId) {
        return (
            <div style={{ backgroundColor: color.panel, color: color.fg, minHeight: '70vh', padding: '2rem' }}>
                <Alert variant="info" className="text-center">
                    <FontAwesomeIcon icon={faWarehouse} size="3x" className="mb-3" style={{ width: 48, color: color.secondary, opacity: 0.5 }} />
                    <h4>Select a Customer First</h4>
                    <p className="mb-0">
                        Please select a customer from the Warehouse Management tab before accessing the Inbound Loads Management.
                    </p>
                </Alert>
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: color.panel, color: color.fg, minHeight: '70vh' }}>

            {/* Tab Navigation */}
            <Row className="mb-4">
                <Col>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button
                            onClick={() => setActiveTab('mixed')}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: activeTab === 'mixed' ? color.primary : 'transparent',
                                color: theme === 'dark' ? '#ffffff' : '#000000',
                                border: `2px solid ${activeTab === 'mixed' ? color.primary : color.border}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                minWidth: '120px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                if (activeTab !== 'mixed') {
                                    e.target.style.borderColor = color.primary;
                                }
                            }}
                            onMouseOut={(e) => {
                                if (activeTab !== 'mixed') {
                                    e.target.style.borderColor = color.border;
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" style={{ 
                                width: '50px',
                                color: `${theme === 'dark' ? '#ffffff' : '#000000'} !important`
                            }} />
                            <span style={{ 
                                color: `${theme === 'dark' ? '#ffffff' : '#000000'} !important`
                            }}>
                                Mixed Load
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('bulk')}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: activeTab === 'bulk' ? color.primary : 'transparent',
                                color: theme === 'dark' ? '#ffffff' : '#000000',
                                border: `2px solid ${activeTab === 'bulk' ? color.primary : color.border}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                minWidth: '120px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                if (activeTab !== 'bulk') {
                                    e.target.style.borderColor = color.primary;
                                }
                            }}
                            onMouseOut={(e) => {
                                if (activeTab !== 'bulk') {
                                    e.target.style.borderColor = color.border;
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faBoxes} className="me-2" style={{ 
                                width: '50px',
                                color: `${theme === 'dark' ? '#ffffff' : '#000000'} !important`
                            }} />
                            <span style={{ 
                                color: `${theme === 'dark' ? '#ffffff' : '#000000'} !important`
                            }}>
                                Bulk Load
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('review')}
                            disabled={inboundItems.length === 0}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: activeTab === 'review' ? color.primary : 'transparent',
                                color: theme === 'dark' ? '#ffffff' : '#000000',
                                border: `2px solid ${activeTab === 'review' ? color.primary : color.border}`,
                                borderRadius: '6px',
                                cursor: inboundItems.length === 0 ? 'not-allowed' : 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                minWidth: '120px',
                                opacity: inboundItems.length === 0 ? 0.5 : 1,
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                if (activeTab !== 'review' && inboundItems.length > 0) {
                                    e.target.style.borderColor = color.primary;
                                }
                            }}
                            onMouseOut={(e) => {
                                if (activeTab !== 'review' && inboundItems.length > 0) {
                                    e.target.style.borderColor = color.border;
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faList} className="me-2" style={{ 
                                width: '50px',
                                color: `${theme === 'dark' ? '#ffffff' : '#000000'} !important`
                            }} />
                            <span style={{ 
                                color: `${theme === 'dark' ? '#ffffff' : '#000000'} !important`
                            }}>
                                Review ({inboundItems.length})
                            </span>
                        </button>
                    </div>
                </Col>
            </Row>

            {/* Selected Customer Display */}
            {selectedCustomerId && (
                <Row className="mb-4">
                    <Col>
                        <div
                            style={{
                                border: `2px solid ${color.success}`,
                                borderRadius: 8,
                                backgroundColor: color.panel,
                                padding: "1rem"
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                                <span style={{ 
                                    color: color.success, 
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold'
                                }}>
                                    ✓
                                </span>
                                <div>
                                    <h4 style={{ margin: 0, color: color.fg }}>
                                        Selected Customer: {getCustomerName(selectedCustomerId)}
                                    </h4>
                                    <p style={{ margin: '0.25rem 0 0 0', opacity: 0.8, fontSize: '0.9rem' }}>
                                        Customer ID: {selectedCustomerId}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            {activeTab === 'mixed' ? (
                <>
                    {selectedCustomerId && (
                        <>
                            {/* Product Selection */}
                            <Row className="mb-4">
                                <Col md={8}>
                                    <Card style={{ 
                                        backgroundColor: color.bg, 
                                        border: `1px solid ${color.border}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        <Card.Header style={{ 
                                            backgroundColor: color.headerBg,
                                            borderBottom: `1px solid ${color.border}`,
                                            color: color.fg
                                        }}>
                                            Product Selection
                                            <Badge bg="primary" className="ms-2" style={{ 
                                                backgroundColor: color.primary, 
                                                color: '#ffffff' 
                                            }}>
                                                {filteredProducts.length} products available
                                            </Badge>
                                        </Card.Header>
                                        <Card.Body>
                                            {/* Single Searchable Product Dropdown */}
                                            <Form.Group>
                                                <Form.Label style={{ color: color.fg, fontWeight: '500' }}>Search & Select Product *</Form.Label>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type="text"
                                                        placeholder="Type to search products (code, name, batch)..."
                                                        value={selectedProductData ? `${selectedProductData.product_name} - ${selectedProductData.product_code}${selectedProductData.batch_no ? ` [${selectedProductData.batch_no}]` : ''}` : searchTerm}
                                                        onChange={handleSearchTermChange}
                                                        style={{
                                                            padding: ".75rem 1rem",
                                                            width: '100%',
                                                            fontSize: "1rem",
                                                            backgroundColor: color.inputBg,
                                                            color: color.fg,
                                                            border: `2px solid ${color.inputBorder}`,
                                                            borderRadius: 6,
                                                            outline: "none",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = color.primary;
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = color.inputBorder;
                                                        }}
                                                    />
                                                    {(searchTerm || selectedProduct) && (
                                                        <button
                                                            onClick={() => {
                                                                setSearchTerm('');
                                                                setSelectedProduct('');
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                right: '10px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                padding: "0.25rem 0.5rem",
                                                                backgroundColor: "transparent",
                                                                color: color.muted,
                                                                border: "none",
                                                                borderRadius: 4,
                                                                cursor: "pointer",
                                                                fontSize: "1rem",
                                                            }}
                                                            title="Clear search"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            </Form.Group>

                                            {/* Filtered Product Results - Show when no product selected OR when searching */}
                                            {filteredProducts.length > 0 && !selectedProduct && (
                                                <div style={{
                                                    marginTop: '10px',
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    border: `1px solid ${color.border}`,
                                                    borderRadius: '6px',
                                                    backgroundColor: color.bg
                                                }}>
                                                    {filteredProducts.slice(0, 10).map((product) => (
                                                        <div
                                                            key={product.id}
                                                            onClick={() => {
                                                                setSelectedProduct(product.id); // Keep as string, don't convert to string
                                                                // Don't set searchTerm to full text - just clear it or keep the search
                                                                setSearchTerm('');
                                                            }}
                                                            style={{
                                                                padding: '10px 15px',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                cursor: 'pointer',
                                                                backgroundColor: selectedProduct === product.id ? color.success : 'transparent',
                                                                color: selectedProduct === product.id ? '#ffffff' : color.fg,
                                                                transition: 'background-color 0.2s ease'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                if (selectedProduct !== product.id) {
                                                                    e.target.style.backgroundColor = color.headerBg;
                                                                }
                                                            }}
                                                            onMouseOut={(e) => {
                                                                if (selectedProduct !== product.id) {
                                                                    e.target.style.backgroundColor = 'transparent';
                                                                }
                                                            }}
                                                        >
                                                            <div style={{ color: color.fg, fontWeight: '500' }}>
                                                                {product.product_name}
                                                            </div>
                                                            <div style={{ color: color.muted, fontSize: '0.9rem' }}>
                                                                Code: {product.product_code}
                                                                {product.batch_no && ` | Batch: ${product.batch_no}`}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {selectedProductData && (
                                                <div className="mt-3 p-3" style={{ 
                                                    backgroundColor: color.headerBg, 
                                                    borderRadius: '4px',
                                                    border: `1px solid ${color.border}`
                                                }}>
                                                    <strong style={{ color: color.fg }}>Selected Product Details:</strong><br />
                                                    <div style={{ color: color.fg, fontSize: '0.9rem', lineHeight: '1.4' }}>
                                                        <strong>Name:</strong> {selectedProductData.product_name}<br />
                                                        <strong>Code:</strong> {selectedProductData.product_code}<br />
                                                        {selectedProductData.batch_no && (
                                                            <><strong>Batch:</strong> {selectedProductData.batch_no}<br /></>
                                                        )}
                                                        <strong>Description:</strong> {selectedProductData.description || 'N/A'}
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Pallet & Quantity Selection */}
                                <Col md={4}>
                                    <Card style={{ 
                                        backgroundColor: color.bg, 
                                        border: `1px solid ${color.border}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        <Card.Header style={{ 
                                            backgroundColor: color.headerBg,
                                            borderBottom: `1px solid ${color.border}`,
                                            color: color.fg
                                        }}>
                                            Pallet & Quantity
                                            <Badge bg="success" className="ms-2" style={{ 
                                                backgroundColor: color.success, 
                                                color: '#ffffff' 
                                            }}>
                                                {emptyPalletsCount} empty ✓
                                            </Badge>
                                        </Card.Header>
                                        <Card.Body>
                                            {/* Pallet Filter */}
                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ color: color.fg, fontWeight: '500', fontSize: '0.9rem' }}>Filter Pallets</Form.Label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder="Filter pallets by code or lane..."
                                                        value={palletFilterTerm}
                                                        onChange={handlePalletFilterChange}
                                                        style={{
                                                            padding: ".375rem .5rem",
                                                            fontSize: ".875rem",
                                                            backgroundColor: color.inputBg,
                                                            color: color.fg,
                                                            border: `1px solid ${color.inputBorder}`,
                                                            borderRadius: 4,
                                                            outline: "none",
                                                            flex: 1
                                                        }}
                                                    />
                                                    {palletFilterTerm && (
                                                        <button
                                                            onClick={() => setPalletFilterTerm("")}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: color.danger,
                                                                cursor: 'pointer',
                                                                fontSize: '.875rem',
                                                                padding: '0 .25rem'
                                                            }}
                                                            title="Clear filter"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ color: color.fg, fontWeight: '500' }}>Pallet *</Form.Label>
                                                <Form.Select 
                                                    value={selectedPalletCode} 
                                                    onChange={(e) => setSelectedPalletCode(e.target.value)}
                                                    disabled={!selectedProduct}
                                                    title={!selectedProduct ? 'Select a product first' : undefined}
                                                    style={{ 
                                                        backgroundColor: color.inputBg, 
                                                        color: color.fg, 
                                                        borderColor: color.inputBorder,
                                                        opacity: !selectedProduct ? 0.5 : 1,
                                                        cursor: !selectedProduct ? 'not-allowed' : 'default'
                                                    }}
                                                >
                                                    <option value="">{!selectedProduct ? 'Select a product first' : 'Select Pallet'}</option>
                                                    {filteredPallets.map((pallet) => {
                                                        const hasInboundItems = inboundItems.some(item => item.pallet_code === pallet.pallet_code);
                                                        const isOccupied = pallet.occupied || (pallet.items && pallet.items.length > 0) || pallet.total_quantity > 0 || hasInboundItems;
                                                        const palletType = pallet.pallet_type || null;
                                                        const isMixedBulk = selectedProductData && (selectedProductData.pallet_spaces || 1) > 1;
                                                        const isBlocked = isMixedBulk && palletType === 'single_sku';
                                                        const statusIcon = isOccupied ? ' 📦' : '';
                                                        const typeLabel = isBlocked ? ` [${palletType} - single SKU only]` : '';
                                                        return (
                                                            <option key={pallet.pallet_id || pallet.id} value={pallet.pallet_code} disabled={isBlocked}
                                                                style={{ color: isBlocked ? '#999' : undefined }}>
                                                                {pallet.pallet_code}{statusIcon}{typeLabel}
                                                            </option>
                                                        );
                                                    })}
                                                </Form.Select>
                                            </Form.Group>

                                            {/* Pallet status information */}
                                            {selectedPalletCode && (() => {
                                                const selectedPallet = filteredPallets.find(p => p.pallet_code === selectedPalletCode);
                                                if (selectedPallet) {
                                                    const inboundItemsCount = inboundItems.filter(item => item.pallet_code === selectedPallet.pallet_code).length;
                                                    const hasExistingStock = selectedPallet.occupied || selectedPallet.total_quantity > 0 || (selectedPallet.items && selectedPallet.items.length > 0);
                                                    const existingQty = selectedPallet.total_quantity || (selectedPallet.items ? selectedPallet.items.length : 0);
                                                    const isEmpty = !hasExistingStock && inboundItemsCount === 0;
                                                    
                                                    return (
                                                        <div style={{
                                                            padding: '0.75rem',
                                                            marginBottom: '1rem',
                                                            backgroundColor: isEmpty ? color.successBg : color.warningBg,
                                                            border: `1px solid ${isEmpty ? color.success : color.warning}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.875rem',
                                                            textAlign: 'center'
                                                        }}>
                                                            <div style={{ 
                                                                fontWeight: 'bold', 
                                                                color: isEmpty ? color.success : color.warning
                                                            }}>
                                                                {isEmpty ? 
                                                                    '✓ Empty Pallet - Ready for Products' : 
                                                                    `📦 Occupied - ${existingQty > 0 ? existingQty + ' units' : 'has stock'} + ${inboundItemsCount} pending`
                                                                }
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}

                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ color: color.fg, fontWeight: '500' }}>Quantity *</Form.Label>
                                                <Form.Control 
                                                    type="number" 
                                                    min="1" 
                                                    value={quantity} 
                                                    onChange={handleQuantityChange}
                                                    placeholder="Enter quantity"
                                                    style={{ 
                                                        backgroundColor: color.inputBg, 
                                                        color: color.fg, 
                                                        borderColor: color.inputBorder
                                                    }}
                                                />
                                            </Form.Group>

                                            <Button 
                                                variant="success" 
                                                className="w-100"
                                                onClick={(e) => {
                                                    logger.log('Button clicked!');
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    addItemToInbound();
                                                }}
                                                disabled={!selectedCustomerId || !selectedProduct || !selectedPalletCode || !quantity}
                                                style={{
                                                    backgroundColor: color.success,
                                                    borderColor: color.success,
                                                    color: '#ffffff',
                                                    opacity: (!selectedCustomerId || !selectedProduct || !selectedPalletCode || !quantity) ? 0.6 : 1
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="me-2" style={{ width: '50px', color: '#ffffff' }} />
                                                Add to Inbound
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Current Inbound Items Table */}
                            {inboundItems.length > 0 && (
                                <Row className="mb-4">
                                    <Col>
                                        <Card style={{ 
                                            backgroundColor: color.bg, 
                                            border: `1px solid ${color.border}`,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <Card.Header style={{ 
                                                backgroundColor: color.headerBg,
                                                borderBottom: `1px solid ${color.border}`,
                                                color: color.fg
                                            }}>
                                                <FontAwesomeIcon icon={faList} className="me-2" style={{ width: '50px', color: color.success }} />
                                                Current Inbound Items
                                                <Badge bg="success" className="ms-2" style={{ 
                                                    backgroundColor: color.success, 
                                                    color: '#ffffff' 
                                                }}>
                                                    {inboundItems.length} items ({totalItems} total units)
                                                </Badge>
                                            </Card.Header>
                                            <Card.Body style={{ padding: '0' }}>
                                                <Table 
                                                    responsive 
                                                    variant={isDark ? "dark" : "light"}
                                                    style={{ 
                                                        margin: 0, 
                                                        backgroundColor: `${color.bg} !important`,
                                                        color: `${color.fg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}
                                                >
                                                    <thead style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>
                                                        <tr>
                                                            <th style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}>Product</th>
                                                            <th style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}>Code</th>
                                                            <th style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}>Batch</th>
                                                            <th style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}>Pallet</th>
                                                            <th style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}>Lane</th>
                                                            <th style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}>Quantity</th>
                                                            <th style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody style={{ 
                                                        backgroundColor: `${color.bg} !important`,
                                                        color: `${color.fg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>
                                                        {inboundItems.map((item) => (
                                                            <tr key={item.id} style={{ 
                                                                backgroundColor: `${color.bg} !important`, 
                                                                color: `${color.fg} !important`,
                                                                borderColor: `${color.border} !important`
                                                            }}>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    color: `${color.fg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>{item.product_name}</td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    color: `${color.fg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>{item.product_code}</td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    color: `${color.fg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>{item.batch_no || '-'}</td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    color: `${color.fg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>{item.pallet_code}</td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    color: `${color.fg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>{item.lane_id || '-'}</td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    color: `${color.fg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>{item.quantity}</td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    borderColor: `${color.border} !important` 
                                                                }}>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => removeItemFromInbound(item.id)}
                                                                        style={{
                                                                            borderColor: color.danger,
                                                                            color: color.danger,
                                                                            backgroundColor: 'transparent'
                                                                        }}
                                                                        onMouseOver={(e) => {
                                                                            e.target.style.backgroundColor = color.danger;
                                                                            e.target.style.color = '#ffffff';
                                                                        }}
                                                                        onMouseOut={(e) => {
                                                                            e.target.style.backgroundColor = 'transparent';
                                                                            e.target.style.color = color.danger;
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} style={{ width: '40px'}} />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>
                                                        <tr style={{
                                                            backgroundColor: `${color.headerBg} !important`,
                                                            color: `${color.headerFg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>
                                                            <td colSpan="5" style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}><strong>Total</strong></td>
                                                            <td style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`, 
                                                                borderColor: `${color.border} !important` 
                                                            }}><strong>{totalItems} units</strong></td>
                                                            <td style={{ 
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                borderColor: `${color.border} !important` 
                                                            }}></td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}
                </>
            ) : activeTab === 'bulk' ? (
                /* Bulk Load Tab */
                <>
                    {selectedCustomerId && (
                        <>
                            {/* Product Selection for Bulk */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Card style={{ 
                                        backgroundColor: color.bg, 
                                        border: `1px solid ${color.border}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        <Card.Header style={{ 
                                            backgroundColor: color.headerBg,
                                            borderBottom: `1px solid ${color.border}`,
                                            color: color.fg
                                        }}>
                                            Product Selection (Single Product)
                                            <Badge bg="primary" className="ms-2" style={{ 
                                                backgroundColor: color.primary, 
                                                color: '#ffffff' 
                                            }}>
                                                {filteredBulkProducts.length} products available
                                            </Badge>
                                        </Card.Header>
                                        <Card.Body>
                                            <Form.Group>
                                                <Form.Label style={{ color: color.fg, fontWeight: '500' }}>Search & Select Product *</Form.Label>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type="text"
                                                        placeholder="Type to search products (code, name, batch)..."
                                                        value={bulkSearchTerm}
                                                        onChange={handleBulkSearchChange}
                                                        style={{
                                                            padding: ".75rem 1rem",
                                                            width: '100%',
                                                            fontSize: "1rem",
                                                            backgroundColor: color.inputBg,
                                                            color: color.fg,
                                                            border: `2px solid ${color.inputBorder}`,
                                                            borderRadius: 6,
                                                            outline: "none",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = color.primary;
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = color.inputBorder;
                                                        }}
                                                    />
                                                    {(bulkSearchTerm || bulkProduct) && (
                                                        <button
                                                            onClick={() => {
                                                                setBulkSearchTerm('');
                                                                setBulkProduct('');
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                right: '10px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                padding: "0.25rem 0.5rem",
                                                                backgroundColor: "transparent",
                                                                color: color.muted,
                                                                border: "none",
                                                                borderRadius: 4,
                                                                cursor: "pointer",
                                                                fontSize: "1rem",
                                                            }}
                                                            title="Clear search"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            </Form.Group>

                                            {/* Filtered Product Results for Bulk */}
                                            {filteredBulkProducts.length > 0 && !bulkProduct && (
                                                <div style={{
                                                    marginTop: '10px',
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    border: `1px solid ${color.border}`,
                                                    borderRadius: '6px',
                                                    backgroundColor: color.bg
                                                }}>
                                                    {filteredBulkProducts.slice(0, 10).map((product) => (
                                                        <div
                                                            key={product.id}
                                                            onClick={() => {
                                                                setBulkProduct(product.id);
                                                                setBulkSearchTerm('');
                                                            }}
                                                            style={{
                                                                padding: '10px 15px',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                cursor: 'pointer',
                                                                backgroundColor: bulkProduct === product.id ? color.success : 'transparent',
                                                                color: bulkProduct === product.id ? '#ffffff' : color.fg,
                                                                transition: 'background-color 0.2s ease'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                if (bulkProduct !== product.id) {
                                                                    e.target.style.backgroundColor = color.headerBg;
                                                                }
                                                            }}
                                                            onMouseOut={(e) => {
                                                                if (bulkProduct !== product.id) {
                                                                    e.target.style.backgroundColor = 'transparent';
                                                                }
                                                            }}
                                                        >
                                                            <div style={{ color: color.fg, fontWeight: '500' }}>
                                                                {product.product_name}
                                                            </div>
                                                            <div style={{ color: color.muted, fontSize: '0.9rem' }}>
                                                                Code: {product.product_code}
                                                                {product.batch_no && ` | Batch: ${product.batch_no}`}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {selectedBulkProductData && (
                                                <div className="mt-3 p-3" style={{ 
                                                    backgroundColor: color.headerBg, 
                                                    borderRadius: '4px',
                                                    border: `1px solid ${color.border}`
                                                }}>
                                                    <strong style={{ color: color.fg }}>Selected Product Details:</strong><br />
                                                    <div style={{ color: color.fg, fontSize: '0.9rem', lineHeight: '1.4' }}>
                                                        <strong>Name:</strong> {selectedBulkProductData.product_name}<br />
                                                        <strong>Code:</strong> {selectedBulkProductData.product_code}<br />
                                                        {selectedBulkProductData.batch_no && (
                                                            <><strong>Batch:</strong> {selectedBulkProductData.batch_no}<br /></>
                                                        )}
                                                        <strong>Description:</strong> {selectedBulkProductData.description || 'N/A'}
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Quantity Configuration */}
                                <Col md={6}>
                                    <Card style={{ 
                                        backgroundColor: color.bg, 
                                        border: `1px solid ${color.border}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        <Card.Header style={{ 
                                            backgroundColor: color.headerBg,
                                            borderBottom: `1px solid ${color.border}`,
                                            color: color.fg
                                        }}>
                                            Quantity Configuration
                                        </Card.Header>
                                        <Card.Body>
                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ color: color.fg, fontWeight: '500' }}>Quantity per Pallet *</Form.Label>
                                                <Form.Control 
                                                    type="number" 
                                                    min="1" 
                                                    value={bulkQuantityPerPallet} 
                                                    onChange={handleBulkQuantityChange}
                                                    placeholder="Enter quantity per pallet"
                                                    style={{ 
                                                        backgroundColor: color.inputBg, 
                                                        color: color.fg, 
                                                        borderColor: color.inputBorder
                                                    }}
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ color: color.fg, fontWeight: '500' }}>Total Pallets (Optional)</Form.Label>
                                                <Form.Control 
                                                    type="number" 
                                                    min="1" 
                                                    value={totalBulkPallets} 
                                                    onChange={handleTotalPalletsChange}
                                                    placeholder="e.g., 26 for a full container"
                                                    style={{ 
                                                        backgroundColor: color.inputBg, 
                                                        color: color.fg, 
                                                        borderColor: color.inputBorder
                                                    }}
                                                />
                                                <Form.Text style={{ color: color.muted, fontSize: '0.8rem' }}>
                                                    This is for reference only - you'll select actual pallets below
                                                </Form.Text>
                                            </Form.Group>

                                            {selectedPallets.length > 0 && bulkQuantityPerPallet && (
                                                <div style={{
                                                    padding: '0.75rem',
                                                    backgroundColor: color.successBg,
                                                    border: `1px solid ${color.success}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    textAlign: 'center'
                                                }}>
                                                    <div style={{ fontWeight: 'bold', color: color.success }}>
                                                        {selectedPallets.length} pallets × {bulkQuantityPerPallet} units = {selectedPallets.length * parseInt(bulkQuantityPerPallet || 0)} total units
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Multiple Pallet Selection */}
                            <Row className="mb-4">
                                <Col>
                                    <Card style={{ 
                                        backgroundColor: color.bg, 
                                        border: `1px solid ${color.border}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        <Card.Header style={{ 
                                            backgroundColor: color.headerBg,
                                            borderBottom: `1px solid ${color.border}`,
                                            color: color.fg
                                        }}>
                                            <FontAwesomeIcon icon={faBoxes} className="me-2" style={{ color: color.primary, width: '50px' }} />
                                            Pallet Selection (Multiple)
                                            <Badge bg="success" className="ms-2" style={{ 
                                                backgroundColor: color.success, 
                                                color: '#ffffff' 
                                            }}>
                                                {selectedPallets.length} selected
                                            </Badge>
                                            <Badge bg="info" className="ms-2" style={{ 
                                                backgroundColor: color.primary, 
                                                color: '#ffffff' 
                                            }}>
                                                {emptyPalletsCount} empty available
                                            </Badge>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm" 
                                                    onClick={selectAllEmptyPallets}
                                                    style={{
                                                        borderColor: color.primary,
                                                        color: color.primary
                                                    }}
                                                >
                                                    Select All Empty Pallets
                                                </Button>
                                                <Button 
                                                    variant="outline-secondary" 
                                                    size="sm" 
                                                    onClick={clearPalletSelection}
                                                    style={{
                                                        borderColor: color.border,
                                                        color: color.fg
                                                    }}
                                                >
                                                    Clear Selection
                                                </Button>
                                                <input
                                                    type="text"
                                                    placeholder="Filter pallets..."
                                                    value={palletFilterTerm}
                                                    onChange={handlePalletFilterChange}
                                                    style={{
                                                        padding: ".25rem .5rem",
                                                        minWidth: 150,
                                                        fontSize: ".875rem",
                                                        backgroundColor: color.inputBg,
                                                        color: color.fg,
                                                        border: `1px solid ${color.border}`,
                                                        borderRadius: 4,
                                                        outline: "none",
                                                    }}
                                                />
                                                {palletFilterTerm && (
                                                    <button
                                                        onClick={() => setPalletFilterTerm("")}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: color.danger,
                                                            cursor: 'pointer',
                                                            fontSize: '.875rem',
                                                            padding: '0 .25rem'
                                                        }}
                                                        title="Clear filter"
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>

                                            <div style={{
                                                maxHeight: '300px',
                                                overflowY: 'auto',
                                                border: `1px solid ${color.border}`,
                                                borderRadius: '6px',
                                                backgroundColor: color.inputBg
                                            }}>
                                                {filteredPallets.length > 100 && (
                                                    <div style={{ 
                                                        padding: '8px', 
                                                        backgroundColor: color.warningBg, 
                                                        fontSize: '0.875rem',
                                                        color: color.fg,
                                                        borderBottom: `1px solid ${color.border}`
                                                    }}>
                                                        ⚡ Showing first 100 of {filteredPallets.length} filtered pallets for performance.
                                                    </div>
                                                )}
                                                <Table 
                                                    hover 
                                                    variant={isDark ? "dark" : "light"}
                                                    style={{ 
                                                        margin: 0,
                                                        backgroundColor: color.inputBg
                                                    }}
                                                >
                                                    <thead style={{ 
                                                        backgroundColor: color.headerBg,
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 1
                                                    }}>
                                                        <tr>
                                                            <th style={{ 
                                                                backgroundColor: color.headerBg,
                                                                color: color.headerFg,
                                                                borderColor: color.border,
                                                                textAlign: 'center',
                                                                width: '60px'
                                                            }}>Select</th>
                                                            <th style={{ 
                                                                backgroundColor: color.headerBg,
                                                                color: color.headerFg,
                                                                borderColor: color.border
                                                            }}>Pallet Code</th>
                                                            <th style={{ 
                                                                backgroundColor: color.headerBg,
                                                                color: color.headerFg,
                                                                borderColor: color.border
                                                            }}>Type</th>
                                                            <th style={{ 
                                                                backgroundColor: color.headerBg,
                                                                color: color.headerFg,
                                                                borderColor: color.border,
                                                                textAlign: 'center'
                                                            }}>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredPallets.slice(0, 100).map((pallet) => {
                                                            const status = palletStatuses.get(pallet.pallet_id || pallet.id) || palletStatuses.get(pallet.pallet_code) || { hasInboundItems: false, isOccupied: false };
                                                            const { hasInboundItems, isOccupied } = status;
                                                            const palletCode = pallet.pallet_code || pallet.pallet_id || pallet.id;
                                                            const palletType = pallet.pallet_type || null;
                                                            const isBulkSelection = selectedBulkProductData && (selectedBulkProductData.pallet_spaces || 1) > 1;
                                                            const isBlocked = isBulkSelection && palletType === 'single_sku';
                                                            // Disable all interaction until a product is selected
                                                            const noProduct = !bulkProduct;
                                                            // Read directly from selectedPallets to guarantee reactive updates
                                                            const isSelected = 
                                                                !isBlocked &&
                                                                !noProduct &&
                                                                palletCode ?
                                                                selectedPallets.includes(palletCode) : false;
                                                            const inInbound = inboundItems.some(item => item.pallet_code === palletCode)
                                                            
                                                            return (
                                                                <tr 
                                                                    key={pallet.pallet_id || pallet.id || pallet.pallet_code}
                                                                    style={{
                                                                        backgroundColor: isBlocked || noProduct ? 'transparent' :
                                                                                       isSelected ? color.highlight : 
                                                                                       isOccupied ? color.warningBg :
                                                                                       inInbound ? color.warningBg : color.inputBg,
                                                                        cursor: isBlocked || noProduct || inInbound ? 'not-allowed' : 'pointer',
                                                                        opacity: isBlocked ? 0.35 : noProduct ? 0.4 : 1,
                                                                    }}
                                                                    onClick={() => !isBlocked && !noProduct && !inInbound && palletCode && togglePalletSelection(palletCode)}
                                                                    title={noProduct ? 'Select a product first' : isBlocked ? `${palletType} pallets cannot hold bulk products` : undefined}
                                                                >
                                                                    <td style={{ 
                                                                        borderColor: color.border,
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            disabled={isBlocked}
                                                                            onChange={() => !isBlocked && togglePalletSelection(palletCode)}
                                                                            style={{ pointerEvents: 'none' }}
                                                                        />
                                                                    </td>
                                                                    <td style={{ 
                                                                        borderColor: color.border,
                                                                        color: color.fg,
                                                                        fontWeight: isSelected ? 'bold' : 'normal'
                                                                    }}>
                                                                        {pallet.pallet_code}
                                                                    </td>
                                                                    <td style={{ 
                                                                        borderColor: color.border,
                                                                        color: isBlocked ? color.danger : palletType === 'standard' ? color.success : color.fg,
                                                                        fontSize: '0.8rem'
                                                                    }}>
                                                                        {palletType || '—'}
                                                                    </td>
                                                                    <td style={{ 
                                                                        borderColor: color.border,
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {isBlocked ? (
                                                                            <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>🚫 Bulk only</Badge>
                                                                        ) : (isOccupied || inInbound) ? (
                                                                            <Badge bg="warning" style={{ fontSize: '0.7rem', color: color.warningBg }}>{inInbound ? '🚚 Inbound' : '📦 Occupied'}</Badge>
                                                                        ) : (
                                                                            <Badge bg="success" style={{ fontSize: '0.7rem' }}>✓ Empty</Badge>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            <div className="mt-3">
                                                <Button 
                                                    variant="success" 
                                                    className="w-100"
                                                    onClick={addBulkLoadToInbound}
                                                    disabled={!selectedCustomerId || !bulkProduct || selectedPallets.length === 0 || !bulkQuantityPerPallet}
                                                    style={{
                                                        backgroundColor: color.success,
                                                        borderColor: color.success,
                                                        color: '#ffffff',
                                                        opacity: (!selectedCustomerId || !bulkProduct || selectedPallets.length === 0 || !bulkQuantityPerPallet) ? 0.6 : 1,
                                                        padding: '12px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faBoxes} className="me-2" style={{ width: '50px', color: '#ffffff' }} />
                                                    Add {selectedPallets.length} Pallets to Inbound
                                                    {selectedPallets.length > 0 && bulkQuantityPerPallet && (
                                                        <span className="ms-2">
                                                            ({selectedPallets.length * parseInt(bulkQuantityPerPallet || 0)} total units)
                                                        </span>
                                                    )}
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Current Inbound Items Table for Bulk */}
                            {inboundItems.length > 0 && (
                                <Row className="mb-4">
                                    <Col>
                                        <Card style={{ 
                                            backgroundColor: color.bg, 
                                            border: `1px solid ${color.border}`,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <Card.Header style={{ 
                                                backgroundColor: color.headerBg,
                                                borderBottom: `1px solid ${color.border}`,
                                                color: color.fg
                                            }}>
                                                <FontAwesomeIcon icon={faList} className="me-2" style={{ width: '50px', color: color.success }} />
                                                Current Inbound Items
                                                <Badge bg="success" className="ms-2" style={{ 
                                                    backgroundColor: color.success, 
                                                    color: '#ffffff' 
                                                }}>
                                                    {inboundItems.length} items ({totalItems} total units)
                                                </Badge>
                                            </Card.Header>
                                            <Card.Body style={{ padding: '0' }}>
                                                <div style={{
                                                    maxHeight: '300px',
                                                    overflowY: 'auto'
                                                }}>
                                                    <Table 
                                                        responsive 
                                                        variant={isDark ? "dark" : "light"}
                                                        style={{ 
                                                            margin: 0, 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}
                                                    >
                                                        <thead style={{ 
                                                            backgroundColor: `${color.headerBg} !important`,
                                                            color: `${color.headerFg} !important`,
                                                            borderColor: `${color.border} !important`,
                                                            position: 'sticky',
                                                            top: 0,
                                                            zIndex: 1
                                                        }}>
                                                            <tr>
                                                                <th style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>Product</th>
                                                                <th style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>Code</th>
                                                                <th style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>Batch</th>
                                                                <th style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>Pallet</th>
                                                                <th style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>Lane</th>
                                                                <th style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>Quantity</th>
                                                                <th style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {inboundItems.map((item) => (
                                                                <tr key={item.id} style={{
                                                                    backgroundColor: `${color.bg} !important`,
                                                                    color: `${color.fg} !important`,
                                                                    borderColor: `${color.border} !important`
                                                                }}>
                                                                    <td style={{ 
                                                                        backgroundColor: `${color.bg} !important`,
                                                                        color: `${color.fg} !important`,
                                                                        borderColor: `${color.border} !important`
                                                                    }}>{item.product_name}</td>
                                                                    <td style={{ 
                                                                        backgroundColor: `${color.bg} !important`,
                                                                        color: `${color.fg} !important`,
                                                                        borderColor: `${color.border} !important`
                                                                    }}>{item.product_code}</td>
                                                                    <td style={{ 
                                                                        backgroundColor: `${color.bg} !important`,
                                                                        color: `${color.fg} !important`,
                                                                        borderColor: `${color.border} !important`
                                                                    }}>{item.batch_no || '-'}</td>
                                                                    <td style={{ 
                                                                        backgroundColor: `${color.bg} !important`,
                                                                        color: `${color.fg} !important`,
                                                                        borderColor: `${color.border} !important`
                                                                    }}>{item.pallet_code}</td>
                                                                    <td style={{ 
                                                                        backgroundColor: `${color.bg} !important`,
                                                                        color: `${color.fg} !important`,
                                                                        borderColor: `${color.border} !important`
                                                                    }}>{item.lane_id || '-'}</td>
                                                                    <td style={{ 
                                                                        backgroundColor: `${color.bg} !important`,
                                                                        color: `${color.fg} !important`,
                                                                        borderColor: `${color.border} !important`
                                                                    }}>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="1"
                                                                            value={item.quantity}
                                                                            onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                                                                            style={{ 
                                                                                width: '80px', 
                                                                                backgroundColor: `${color.bg} !important`, 
                                                                                color: `${color.fg} !important`, 
                                                                                borderColor: `${color.border} !important` 
                                                                            }}
                                                                        />
                                                                    </td>
                                                                    <td style={{ 
                                                                        backgroundColor: `${color.bg} !important`,
                                                                        color: `${color.fg} !important`,
                                                                        borderColor: `${color.border} !important`
                                                                    }}>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => removeItemFromInbound(item.id)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} style={{ width: '40px' }} />
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot style={{ 
                                                            backgroundColor: `${color.headerBg} !important`,
                                                            color: `${color.headerFg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>
                                                            <tr style={{
                                                                backgroundColor: `${color.headerBg} !important`,
                                                                color: `${color.headerFg} !important`,
                                                                borderColor: `${color.border} !important`
                                                            }}>
                                                                <td colSpan="5" style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`,
                                                                    borderColor: `${color.border} !important` 
                                                                }}><strong>Total</strong></td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    color: `${color.headerFg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}><strong>{totalItems} units</strong></td>
                                                                <td style={{ 
                                                                    backgroundColor: `${color.headerBg} !important`,
                                                                    borderColor: `${color.border} !important` 
                                                                }}></td>
                                                            </tr>
                                                        </tfoot>
                                                    </Table>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}
                </>
            ) : (
                /* Review Tab */
                <Row>
                    <Col>
                        <Card style={{ backgroundColor: color.bg, border: `1px solid ${color.border}` }}>
                            <Card.Header style={{ 
                                backgroundColor: color.headerBg,
                                borderBottom: `1px solid ${color.border}`,
                                color: color.fg
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '1rem'
                                }}>
                                    <h5 className="mb-0">Review Inbound Items</h5>
                                    
                                    {/* Date Display/Picker */}
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.5rem',
                                        fontSize: '1rem',
                                        fontWeight: 'bold'
                                    }}>
                                        <span>Date:</span>
                                        {isAdmin ? (
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="date"
                                                    value={inboundDate}
                                                    onChange={handleDateChange}
                                                    style={{
                                                        padding: '0.25rem 0.5rem',
                                                        border: `1px solid ${color.border}`,
                                                        borderRadius: '4px',
                                                        backgroundColor: color.inputBg,
                                                        color: color.fg,
                                                        fontSize: '0.9rem',
                                                        opacity: 0, // Hide the native date input
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <span style={{ 
                                                    color: color.primary,
                                                    backgroundColor: color.panel,
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '4px',
                                                    border: `1px solid ${color.border}`,
                                                    cursor: 'pointer',
                                                    display: 'inline-block',
                                                    minWidth: '100px',
                                                    position: 'relative',
                                                    pointerEvents: 'none' // Allow clicks to pass through to the date input
                                                }}>
                                                    {formatDateForDisplay(inboundDate)} 📅
                                                </span>
                                            </div>
                                        ) : (
                                            <span style={{ 
                                                color: color.primary,
                                                backgroundColor: color.panel,
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '4px',
                                                border: `1px solid ${color.border}`
                                            }}>
                                                {formatDateForDisplay(inboundDate)}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {inboundItems.length > 0 && (
                                        <Badge bg="success" className="ms-2" style={{ 
                                            backgroundColor: color.success, 
                                            color: '#ffffff' 
                                        }}>
                                            {inboundItems.length} items ({totalItems} total units)
                                        </Badge>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {inboundItems.length === 0 ? (
                                    <Alert variant="info">
                                        No items added yet. Switch to the "Add Items" tab to add products.
                                    </Alert>
                                ) : (
                                    <>
                                        <Table 
                                            responsive 
                                            variant={isDark ? "dark" : "light"}
                                            style={{ 
                                                margin: 0,
                                                backgroundColor: `${color.bg} !important`,
                                                color: `${color.fg} !important`,
                                                borderColor: `${color.border} !important`
                                            }}
                                        >
                                            <thead style={{ 
                                                backgroundColor: `${color.headerBg} !important`,
                                                color: `${color.headerFg} !important`,
                                                borderColor: `${color.border} !important`
                                            }}>
                                                <tr>
                                                    <th style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>Product</th>
                                                    <th style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>Code</th>
                                                    <th style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>Batch</th>
                                                    <th style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>Pallet</th>
                                                    <th style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>Lane</th>
                                                    <th style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>Quantity</th>
                                                    <th style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inboundItems.map((item) => (
                                                    <tr key={item.id} style={{
                                                        backgroundColor: `${color.bg} !important`,
                                                        color: `${color.fg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}>
                                                        <td style={{ 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>{item.product_name}</td>
                                                        <td style={{ 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>{item.product_code}</td>
                                                        <td style={{ 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>{item.batch_no || '-'}</td>
                                                        <td style={{ 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>{item.pallet_code}</td>
                                                        <td style={{ 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>{item.lane_id || '-'}</td>
                                                        <td style={{ 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>
                                                            <Form.Control
                                                                type="number"
                                                                min="1"
                                                                value={item.quantity}
                                                                onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                                                                style={{ 
                                                                    width: '80px', 
                                                                    backgroundColor: `${color.bg} !important`, 
                                                                    color: `${color.fg} !important`, 
                                                                    borderColor: `${color.border} !important` 
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={{ 
                                                            backgroundColor: `${color.bg} !important`,
                                                            color: `${color.fg} !important`,
                                                            borderColor: `${color.border} !important`
                                                        }}>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => removeItemFromInbound(item.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} style={{ width: '40px' }} />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot style={{ 
                                                backgroundColor: `${color.headerBg} !important`,
                                                color: `${color.headerFg} !important`,
                                                borderColor: `${color.border} !important`
                                            }}>
                                                <tr style={{
                                                    backgroundColor: `${color.headerBg} !important`,
                                                    color: `${color.headerFg} !important`,
                                                    borderColor: `${color.border} !important`
                                                }}>
                                                    <td colSpan="5" style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}><strong>Total</strong></td>
                                                    <td style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}><strong>{totalItems} units</strong></td>
                                                    <td style={{ 
                                                        backgroundColor: `${color.headerBg} !important`,
                                                        color: `${color.headerFg} !important`,
                                                        borderColor: `${color.border} !important`
                                                    }}></td>
                                                </tr>
                                            </tfoot>
                                        </Table>

                                        {/* Additional Costs Section */}
                                        <Card className="mt-4" style={{
                                            backgroundColor: color.bg,
                                            border: `1px solid ${color.border}`,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <Card.Header style={{
                                                backgroundColor: color.headerBg,
                                                borderBottom: `1px solid ${color.border}`,
                                                color: color.headerFg
                                            }}>
                                                <FontAwesomeIcon icon={faBoxes} className="me-2" style={{ width: '50px', color: color.primary }} />
                                                Additional Services
                                            </Card.Header>
                                            <Card.Body style={{ 
                                                padding: '1rem',
                                                backgroundColor: color.bg,
                                                color: color.fg
                                            }}>
                                                <Row>
                                                    {/* Left Column - Pallet Services */}
                                                    <Col md={6}>
                                                        <h6 style={{ 
                                                            marginBottom: '1rem', 
                                                            color: color.fg,
                                                            fontWeight: 'bold'
                                                        }}>Pallet Services</h6>
                                                        
                                                        {/* Supplied Pallets */}
                                                        <div className="mb-3">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <Form.Check 
                                                                    type="checkbox"
                                                                    id="suppliedPallets"
                                                                    label="Supplied Pallets"
                                                                    checked={additionalCosts.suppliedPallets}
                                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                                        ...prev,
                                                                        suppliedPallets: e.target.checked,
                                                                        suppliedPalletsQty: e.target.checked ? (prev.suppliedPalletsQty || 1) : 0
                                                                    }))}
                                                                    style={{
                                                                        color: color.fg,
                                                                        margin: 0
                                                                    }}
                                                                />
                                                                {additionalCosts.suppliedPallets && (
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={additionalCosts.suppliedPalletsQty || 1}
                                                                        onChange={(e) => setAdditionalCosts(prev => ({
                                                                            ...prev,
                                                                            suppliedPalletsQty: parseInt(e.target.value) || 1
                                                                        }))}
                                                                        placeholder="Qty"
                                                                        style={{
                                                                            backgroundColor: color.inputBg,
                                                                            borderColor: color.inputBorder,
                                                                            color: color.fg,
                                                                            width: '80px'
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Handled Pallets */}
                                                        <div className="mb-3">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <Form.Check 
                                                                    type="checkbox"
                                                                    id="handledPallets"
                                                                    label="Handled Pallets"
                                                                    checked={additionalCosts.handledPallets}
                                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                                        ...prev,
                                                                        handledPallets: e.target.checked,
                                                                        handledPalletsQty: e.target.checked ? (prev.handledPalletsQty || 1) : 0
                                                                    }))}
                                                                    style={{
                                                                        color: color.fg,
                                                                        margin: 0
                                                                    }}
                                                                />
                                                                {additionalCosts.handledPallets && (
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={additionalCosts.handledPalletsQty || 1}
                                                                        onChange={(e) => setAdditionalCosts(prev => ({
                                                                            ...prev,
                                                                            handledPalletsQty: parseInt(e.target.value) || 1
                                                                        }))}
                                                                        placeholder="Qty"
                                                                        style={{
                                                                            backgroundColor: color.inputBg,
                                                                            borderColor: color.inputBorder,
                                                                            color: color.fg,
                                                                            width: '80px'
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Wrapped Pallets */}
                                                        <div className="mb-3">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <Form.Check 
                                                                    type="checkbox"
                                                                    id="wrappedPallets"
                                                                    label="Wrapped Pallets"
                                                                    checked={additionalCosts.wrappedPallets}
                                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                                        ...prev,
                                                                        wrappedPallets: e.target.checked,
                                                                        wrappedPalletsQty: e.target.checked ? (prev.wrappedPalletsQty || 1) : 0
                                                                    }))}
                                                                    style={{
                                                                        color: color.fg,
                                                                        margin: 0
                                                                    }}
                                                                />
                                                                {additionalCosts.wrappedPallets && (
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={additionalCosts.wrappedPalletsQty || 1}
                                                                        onChange={(e) => setAdditionalCosts(prev => ({
                                                                            ...prev,
                                                                            wrappedPalletsQty: parseInt(e.target.value) || 1
                                                                        }))}
                                                                        placeholder="Qty"
                                                                        style={{
                                                                            backgroundColor: color.inputBg,
                                                                            borderColor: color.inputBorder,
                                                                            color: color.fg,
                                                                            width: '80px'
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Banded Pallets */}
                                                        <div className="mb-3">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <Form.Check 
                                                                    type="checkbox"
                                                                    id="bandedPallets"
                                                                    label="Banded Pallets"
                                                                    checked={additionalCosts.bandedPallets}
                                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                                        ...prev,
                                                                        bandedPallets: e.target.checked,
                                                                        bandedPalletsQty: e.target.checked ? (prev.bandedPalletsQty || 1) : 0
                                                                    }))}
                                                                    style={{
                                                                        color: color.fg,
                                                                        margin: 0
                                                                    }}
                                                                />
                                                                {additionalCosts.bandedPallets && (
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={additionalCosts.bandedPalletsQty || 1}
                                                                        onChange={(e) => setAdditionalCosts(prev => ({
                                                                            ...prev,
                                                                            bandedPalletsQty: parseInt(e.target.value) || 1
                                                                        }))}
                                                                        placeholder="Qty"
                                                                        style={{
                                                                            backgroundColor: color.inputBg,
                                                                            borderColor: color.inputBorder,
                                                                            color: color.fg,
                                                                            width: '80px'
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>

                                                    {/* Right Column - Container Options */}
                                                    <Col md={6}>
                                                        <h6 style={{ 
                                                            marginBottom: '1rem', 
                                                            color: color.fg,
                                                            fontWeight: 'bold'
                                                        }}>Container Type</h6>
                                                        
                                                        {/* 20ft Container */}
                                                        <Form.Check 
                                                            type="radio"
                                                            name="containerType"
                                                            id="container20ft"
                                                            label="20ft Container"
                                                            value="20ft"
                                                            checked={additionalCosts.containerType === '20ft'}
                                                            onChange={(e) => setAdditionalCosts(prev => ({
                                                                ...prev,
                                                                containerType: e.target.value
                                                            }))}
                                                            className="mb-2"
                                                            style={{
                                                                color: color.fg
                                                            }}
                                                        />

                                                        {/* 40ft Container */}
                                                        <Form.Check 
                                                            type="radio"
                                                            name="containerType"
                                                            id="container40ft"
                                                            label="40ft Container"
                                                            value="40ft"
                                                            checked={additionalCosts.containerType === '40ft'}
                                                            onChange={(e) => setAdditionalCosts(prev => ({
                                                                ...prev,
                                                                containerType: e.target.value
                                                            }))}
                                                            className="mb-2"
                                                            style={{
                                                                color: color.fg
                                                            }}
                                                        />

                                                        {/* Clear Container Selection */}
                                                        {additionalCosts.containerType && (
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                onClick={() => setAdditionalCosts(prev => ({
                                                                    ...prev,
                                                                    containerType: null
                                                                }))}
                                                                style={{
                                                                    marginTop: '0.5rem',
                                                                    fontSize: '0.8rem',
                                                                    backgroundColor: 'transparent',
                                                                    borderColor: color.border,
                                                                    color: color.fg
                                                                }}
                                                            >
                                                                Clear Container Selection
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Row>

                                                <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${color.border}` }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <div>
                                                            <h6 className="mb-1" style={{ color: color.fg, fontWeight: 'bold' }}>
                                                                Additional Charges
                                                            </h6>
                                                            <small style={{ color: color.muted }}>
                                                                One-off chargeable work for this inbound load
                                                            </small>
                                                        </div>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={addCustomCharge}
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className="me-2" style={{ width: '16px' }} />
                                                            Add Charge
                                                        </Button>
                                                    </div>

                                                    {additionalCosts.customCharges.map(charge => (
                                                        <Row key={charge.id} className="g-2 align-items-end mb-2">
                                                            <Col md={7}>
                                                                <Form.Group>
                                                                    <Form.Label>Description</Form.Label>
                                                                    <Form.Control
                                                                        type="text"
                                                                        maxLength={200}
                                                                        placeholder="e.g. Pallet rework"
                                                                        value={charge.description}
                                                                        onChange={(e) => updateCustomCharge(
                                                                            charge.id, 'description', e.target.value
                                                                        )}
                                                                        isInvalid={!charge.description.trim()}
                                                                        style={{
                                                                            backgroundColor: color.inputBg,
                                                                            borderColor: color.inputBorder,
                                                                            color: color.fg
                                                                        }}
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={4}>
                                                                <Form.Group>
                                                                    <Form.Label>Price</Form.Label>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <span style={{ color: color.fg }}>£</span>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0.01"
                                                                            step="0.01"
                                                                            placeholder="0.00"
                                                                            value={charge.amount}
                                                                            onChange={(e) => updateCustomCharge(
                                                                                charge.id, 'amount', e.target.value
                                                                            )}
                                                                            isInvalid={!charge.amount || Number(charge.amount) <= 0}
                                                                            style={{
                                                                                backgroundColor: color.inputBg,
                                                                                borderColor: color.inputBorder,
                                                                                color: color.fg
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={1}>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    onClick={() => removeCustomCharge(charge.id)}
                                                                    title="Remove charge"
                                                                    aria-label="Remove charge"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} style={{ width: '16px' }} />
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    ))}

                                                    {additionalCosts.customCharges.length > 0 && (
                                                        <div className="text-end mt-3" style={{ color: color.fg }}>
                                                            <strong>Additional charges: £{customChargesTotal.toFixed(2)}</strong>
                                                        </div>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        <div className="mt-4 text-center">
                                            <Button 
                                                variant="success" 
                                                size="lg"
                                                onClick={handleSubmitBatch}
                                                disabled={inboundItems.length === 0 || hasInvalidCustomCharges || loading}
                                                style={{
                                                    backgroundColor: color.success,
                                                    borderColor: color.success,
                                                    color: '#ffffff',
                                                    fontWeight: '500',
                                                    padding: '12px 24px'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faBoxes} className="me-2" style={{ width: '50px', color: '#ffffff' }} />
                                                {loading ? `Processing Items...` : `Submit ${inboundItems.length} Items`}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Activity Indicator Overlay */}
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: color.bg,
                        padding: '30px',
                        borderRadius: '10px',
                        textAlign: 'center',
                        border: `2px solid ${color.border}`,
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <ActivityIndicator />
                        <p style={{ 
                            color: color.fg, 
                            marginTop: '15px', 
                            marginBottom: 0,
                            fontSize: '16px',
                            fontWeight: '500'
                        }}>
                            Processing inbound items...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InboundLoadsManagement;
